import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  OrganizationAuthMethodEnum,
  usersLoginAdminCreateMutation
} from 'src/api/main'
import { AppLoadingScreen, ErrorBoundary } from 'src/components'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import {
  getApiErrorMessages,
  getSocialAuthRedirectOrigin,
  setLocalStorageToken
} from 'src/utils'

import { useProviderCallback } from '../hooks'
import { providerService } from '../providers'
import {
  asAuthenticatedTokens,
  isMfaRequired
} from '../utils/authResponseAdapters'

export const AppleCallback = () => {
  const { message } = App.useApp()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { updateUserData } = useSessionContext()

  // This spinner page has no form to attach an Alert to, so failures are handed
  // to the login screen, which renders them inline (see Login's seeding effect).
  const goToLoginWithError = useCallback(
    (error: unknown) =>
      navigate(paths.auth.login, {
        replace: true,
        state: { authErrors: getApiErrorMessages(error) }
      }),
    [navigate]
  )

  const { mutateAsync: fetchUserAfterLogin } = useMutation({
    ...usersLoginAdminCreateMutation(),
    onSuccess: (response) => {
      updateUserData(response)
      message.success(t('login_success'))
      navigate(paths.home)
    },
    onError: goToLoginWithError
  })

  const handleProcess = useCallback(
    async ({
      code,
      state,
      user
    }: {
      code: string
      state: string
      user: string | null
    }) => {
      try {
        const response = await providerService.processCallback({
          provider: OrganizationAuthMethodEnum.APPLE,
          flow: 'login',
          code,
          state,
          redirectUri: `${getSocialAuthRedirectOrigin()}${paths.auth.appleClientCallback}`,
          user: user ?? undefined
        })

        const tokens = asAuthenticatedTokens(response)
        if (tokens) {
          setLocalStorageToken(tokens)
          await fetchUserAfterLogin({})
          return
        }

        if (isMfaRequired(response)) {
          message.info(t('auth.mfaRequired'))
          return
        }

        message.error(t('auth.appleCallbackMissingParams'))
        navigate(paths.auth.login)
      } catch (e) {
        const error = e as { response?: { data?: { code?: string } } }
        const code = error.response?.data?.code
        if (code === 'apple_not_linked') {
          navigate(`${paths.auth.login}?apple_error=apple_not_linked`, {
            replace: true
          })
          return
        }
        goToLoginWithError(error)
      }
    },
    [fetchUserAfterLogin, goToLoginWithError, message, navigate, t]
  )

  useProviderCallback({
    provider: OrganizationAuthMethodEnum.APPLE,
    flow: 'login',
    searchParams,
    navigate,
    t,
    missingParamsKey: 'auth.appleCallbackMissingParams',
    fallbackPath: paths.auth.login,
    onProcess: handleProcess
  })

  return (
    <ErrorBoundary>
      <AppLoadingScreen />
    </ErrorBoundary>
  )
}
